/* eslint-disable @typescript-eslint/naming-convention */
import type { TranslationTag } from '@ps/web-core-intl';
import { canUseDOM } from '@ps/web-core-utils';
import type { MatchInfo, Reference } from '@ps/web-martech-cms-types';
import { Translation } from '@ps/web-shared-components';
import classNames from 'clsx';
import { format } from 'date-fns';
import React from 'react';

import { CMSHTML, CMSLink, useCMSReference } from '@swf/cms';

import { TXTWCL_Read_more } from './MatchGridItem.messages.json';
import styles from './MatchGridItem.scss';

const getHtmlText = (html: string) => {
  if (!html) return '';
  if (canUseDOM) {
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText;
  }
  return '';
};

const descriptionMaxLength = 70;

interface Props {
  itemRef: Reference;
  previewTranslation: TranslationTag;
  reportTranslation: TranslationTag;
}

const MatchGridItem: React.FC<Props> = ({ itemRef, previewTranslation, reportTranslation }) => {
  const matchInfo = useCMSReference<MatchInfo>(itemRef);

  if (!matchInfo) {
    return null;
  }

  const {
    info_type,
    game_date,
    content: {
      scoreline_segment,
      main_title,
      headline,
      main_headline,
      game_summary,
      intro_segment,
      team_page_url
    }
  } = matchInfo;

  const postgame = info_type === 'postgame';
  const type = postgame ? (
    <Translation {...reportTranslation} />
  ) : (
    <Translation {...previewTranslation} />
  );
  const date = format(new Date(game_date), 'LLLL d');
  const title = getHtmlText((postgame ? scoreline_segment : main_title) as string);
  const contentHeadline = getHtmlText((postgame ? headline : main_headline) as string);
  let description = getHtmlText((postgame ? game_summary : intro_segment) as string);

  if (description.length > descriptionMaxLength) {
    description = `${description.slice(0, descriptionMaxLength)}...`;
  }

  return (
    <article>
      <div className={classNames(styles.header, postgame ? styles.report : styles.preview)}>
        <p>
          {type} | {date}
        </p>
        <CMSHTML content={title} className={styles.itemTitle} />
      </div>
      <CMSHTML content={contentHeadline} className={styles.contentHeadline} />
      <CMSHTML content={description} className={styles.description} />
      {team_page_url && (
        <CMSLink target="_self" url={team_page_url as string}>
          [<Translation {...TXTWCL_Read_more} />]
        </CMSLink>
      )}
    </article>
  );
};

export default MatchGridItem;
